
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Ref } from "vue-property-decorator";
    import { VForm } from "@/vForm";
    import * as toastr from "toastr";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import FileUpload from "@/vue/components/FileUpload.vue";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { IPathway, Pathway } from "@/model/Pathway";

    @Component({ components: { ApiButton, ConfirmDialogue, DatePicker, FileUpload } })
    export default class PathwayDialogue extends Vue {

        //
        // -- properties
        //

        @Ref("pathwayForm") private readonly pathwayForm!: VForm;

        showDialogue: boolean = false;
        dialogueTitle: string = "";
        pathway: Pathway = new Pathway();

        //
        // -- computed properties
        //

        get isNew(): boolean {
            return Utils.isEmptyId(this.pathway.id);
        }

        get isAllowAnyMark(): boolean {
            return this.pathway.allowedWrongAnswers >= 9999;
        }
        set isAllowAnyMark(value: boolean) {
            this.pathway.allowedWrongAnswers = value ? 9999 : 0;
        }

        get allowedWrongAnswersText(): string {
            return this.isAllowAnyMark ? "" : this.pathway.allowedWrongAnswers.toString();
        }
        set allowedWrongAnswersText(value: string) {
            this.pathway.allowedWrongAnswers = +value;
        }


        //
        // -- methods
        //

        add(briefingID: string): void {
            this.reset(briefingID);

            this.dialogueTitle = "Add Pathway";
            this.showDialogue = true;
        }

        async edit(id: string): Promise<void> {
            this.reset(Utils.emptyGuidValue);
            this.dialogueTitle = "Edit Pathway";
            const pathwayData: IPathway = await ApiClient.get(`api/pathway/Load?id=${id}`);
            this.pathway.update(pathwayData);
            this.showDialogue = true;
        }

        reset(briefingID: string): void {
            (this.$refs.fileUpload as FileUpload)?.clear();
            Utils.resetObject(this.pathway);
            this.pathway.briefingID = briefingID;
            this.pathway.allowedWrongAnswers = 9999; // allow all by default
            this.pathwayForm?.resetValidation();
        }

        goToPreview(): void {
            this.$emit("go-to-preview", this.pathway);
        }

        private async save(): Promise<void> {
            if (!this.pathwayForm.validate()) {
                toastr.warning("Please fix highlighted issues", "Cannot Save");
                return;
            }
            const response: ISaveResponse = await ApiClient.post("/api/pathway/save", this.pathway, "pathway-save");
            if(this.pathway.isNew) this.pathway.id = response.newID;
            toastr.success("Saved");
            this.$emit("saved", this.pathway.id);
            this.showDialogue = false;
        }

        private confirmDelete(): void {
            const dialog: ConfirmDialogue = this.$refs.deleteDialogue as ConfirmDialogue;
            dialog.show();
        }

        private async doDelete(): Promise<void> {
            await ApiClient.post("/api/pathway/delete", this.pathway, "pathway-delete");
            toastr.warning("Deleted");
            this.$emit("saved");
            this.showDialogue = false;
        }
    }
